/**
 * 랜딩 인페이지 데모 통화 — data-demo-call="<slug>" 버튼을 누르면 페이지 이동 없이
 * 그 자리에서 바로 음성 통화가 시작된다 (공개 try API + LiveKit, TryPage 와 동일 플로우).
 *
 * 슬러그 → share link 토큰 매핑은 런타임 env `VITE_DEMO_LINKS` 로 주입한다
 * (docker/entrypoint.sh → window.ENV, 형식: "slug:token,slug:token" — JSON 을 피한 건
 * env-config.js 의 쌍따옴표 이스케이프 문제 때문). 매핑이 없는 슬러그는 preventDefault
 * 하지 않아 기존 href(문의 폼)로 동작한다 — env 를 채우기 전까지는 다크 상태.
 *
 * 통화 중에는 우하단에 "에이전트와 통화하고 있어요" 카드가 뜨고, 통화가 끝나면
 * **그 카드 안에서** 세션 리포트로 바뀐다 — 통화 중과 종료 후가 같은 자리에서
 * 이어지고 팝업이 늘어나지 않는다. 홈 데모 패널에서 시작한 통화는 패널 자체가
 * 그 역할을 하므로 카드를 만들지 않는다(패널 + 카드로 두 개가 되는 걸 피한다).
 *
 * 통화 종료 후에는 세션 리포트(스펙 "산출물(종료 시)")를 보여준다:
 *  - 홈 데모 패널에서 끝내면 → 같은 패널 안에서 리포트로 전환 (팝업 1개 유지)
 *  - 패널 × 로 끊거나 유즈케이스 페이지면 → 우하단 단독 카드(통화 중 카드를 이어받음)
 * 리포트에는 "다시 대화하기" 통로가 항상 포함된다.
 * 리포트가 없는 통화(8초 미만·리포트 미지원 데모)는 카드를 그냥 접는다.
 *
 * livekit-client 는 첫 클릭 때 동적 import 되므로 초기 페이지 로드에 얹는 비용은
 * 이 로더 한 개뿐이다.
 */
import type { Participant, RemoteTrack, Room } from 'livekit-client'

import { sendLandingEvent } from '../lib/analytics'

type EnvHolder = { ENV?: Record<string, string> }

// VITE_DEMO_API_URL: 데모 통화 API 만 다른 곳으로 보낼 때 (로컬 vite dev 에서
// .env.local 로 same-origin 을 가리키면 vite 프록시가 try API 를 dev 백엔드로 우회
// — 로컬 스택엔 core 가 없어 실통화 불가 + dev CORS 가 localhost:3000 을 막기 때문).
const API_BASE =
  (
    (globalThis as EnvHolder).ENV?.VITE_DEMO_API_URL ||
    import.meta.env.VITE_DEMO_API_URL ||
    (globalThis as EnvHolder).ENV?.VITE_API_URL ||
    import.meta.env.VITE_API_URL ||
    'http://localhost:8000/api'
  ).replace(/\/api\/?$/, '') + '/api/v1'

function demoLinks(): Record<string, string> {
  const raw =
    (globalThis as EnvHolder).ENV?.VITE_DEMO_LINKS ||
    import.meta.env.VITE_DEMO_LINKS ||
    ''
  const map: Record<string, string> = {}
  for (const pair of String(raw).split(',')) {
    const i = pair.indexOf(':')
    if (i > 0) map[pair.slice(0, i).trim()] = pair.slice(i + 1).trim()
  }
  return map
}

/* ── GA4 — 이벤트 정의 시트("[Neona Agent] Events" · 이벤트정의(랜딩) 탭) 기준 ──
 *
 * 통화는 LiveKit 세션 상태라 DOM 에 드러나지 않는다 → GTM 클릭 트리거로는 잡을 수 없고
 * 여기서 직접 쏜다. 시트가 요구하는 세 지점: 시작 · 30/60초 지속 · 종료.
 *
 * source 로 유입을 가른다 — 랜딩 인페이지 데모(landing_demo)와 공유링크 통화(share_link).
 * page 파라미터는 analytics.js 가 계산해 window.NEONA_PAGE 로 올려둔다(같은 규칙 1벌 유지).
 */
function ga(name: string, params: Record<string, string | number>) {
  // 전송은 중앙 초크포인트(lib/analytics.ts)를 지난다 — 직접 gtag 를 부르면
  // scrubParams 를 우회해 민감값 정제가 빠진다(CodeRabbit 지적).
  const page = (globalThis as { NEONA_PAGE?: string }).NEONA_PAGE || 'other'
  sendLandingEvent(name, { ...params, source: 'landing_demo', page })
}

/** 시트 try_call_end 의 end_reason 값. cleanupCall 4종 + 언로드 경로. */
export const END_REASONS = ['button', 'close', 'disconnected', 'error', 'pagehide'] as const

/** 통화 지속 지점 — 시트 try_call_progress 의 sec 값. */
const PROGRESS_MARKS = [30, 60]

interface ActiveCall {
  room: Room
  endUrl: string
  btn: HTMLElement
  originalHtml: string
  startedAt: number
  slug: string
  timerId: number
  orbEl: Element | null
}

let active: ActiveCall | null = null
// 방문자 소유 증명(TASK-16402) — 링크 토큰은 이 페이지에 공개돼 있어 그것만으로는 "이
// 방문자의 통화"임을 증명하지 못한다. 세션을 만들 때 받은 토큰을 종료·리포트 요청에 싣는다.
// 키는 그 세션의 end·report 주소 — 호출부가 주소만 들고 다녀서 시그니처를 늘리지 않는다.
const visitorTokenByUrl = new Map<string, string>()

function visitorHeaders(url: string): Record<string, string> {
  const t = visitorTokenByUrl.get(url)
  return t ? { 'X-Neona-Visitor-Token': t } : {}
}

/** 종료 요청 본문 — 서버가 헤더 대신 받는다(text/plain 이라 preflight 가 없다). */
function endBody(endUrl: string): Blob | undefined {
  const t = visitorTokenByUrl.get(endUrl)
  return t ? new Blob([JSON.stringify({ visitor_token: t })], { type: 'text/plain' }) : undefined
}
let busy = false
/** 통화 중 카드 — 종료 시 같은 DOM 을 리포트 카드가 이어받는다(showReportCard). */
let liveCard: HTMLElement | null = null

/* ── 스타일 — 통화 중 상태 + 세션 리포트 (랜딩 톤: Pretendard·그린 #16a34a·화이트 카드) ── */

const STYLE_ID = 'ndc-style'
function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return
  const s = document.createElement('style')
  s.id = STYLE_ID
  s.textContent = [
    // 통화 중 버튼 상태
    '@keyframes ndc-pulse{0%{box-shadow:0 0 0 0 rgba(255,255,255,.55)}70%{box-shadow:0 0 0 7px rgba(255,255,255,0)}100%{box-shadow:0 0 0 0 rgba(255,255,255,0)}}',
    '@keyframes ndc-orb{0%,100%{transform:scale(1)}50%{transform:scale(1.07)}}',
    '@keyframes ndc-blink{0%,100%{opacity:1}50%{opacity:.35}}',
    '.ndc-dot{display:inline-block;width:8px;height:8px;border-radius:50%;background:#fff;margin-right:6px;vertical-align:1px;animation:ndc-pulse 1.6s ease-out infinite}',
    '.ndc-dot--speaking{animation:ndc-pulse .7s ease-out infinite,ndc-blink .7s ease-in-out infinite}',
    '.ndc-orb-live{animation:ndc-orb 1.6s ease-in-out infinite}',
    '.ndc-status{font-weight:400;opacity:.85}',
    // 리포트 — 공통 콘텐츠 (.ndc-rep) + 표면 2종 (플로팅 카드 / 홈 패널 내부)
    '@keyframes ndc-rise{from{opacity:0;transform:translateY(12px) scale(.98)}to{opacity:1;transform:none}}',
    '@keyframes ndc-fadeup{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}',
    '@keyframes ndc-grow{from{width:0}}',
    '@keyframes ndc-shimmer{0%{background-position:-200px 0}100%{background-position:200px 0}}',
    '.ndc-card{position:fixed;right:24px;bottom:24px;z-index:300;width:420px;max-width:calc(100vw - 32px);max-height:min(76vh,640px);display:flex;flex-direction:column;background:#fff;color:#0b0b0c;border:1px solid rgba(11,11,12,.08);border-radius:22px;box-shadow:0 8px 24px -12px rgba(11,11,12,.18),0 32px 80px -24px rgba(11,11,12,.35);animation:ndc-rise .32s cubic-bezier(.22,1,.36,1);overflow:hidden}',
    '@media (max-width:640px){.ndc-card{left:14px;right:14px;bottom:14px;width:auto}}',
    '.ndc-card-scroll{overflow-y:auto;padding:22px 22px 0;flex:1;min-height:0}',
    '.ndc-inpanel{padding:22px 22px 0;background:#fafafb;max-height:min(58vh,520px);display:flex;flex-direction:column}',
    '.ndc-inpanel .ndc-rep{flex:1;min-height:0;overflow-y:auto;margin:0 -22px;padding:0 22px}',
    '.ndc-rep-foot{flex:none;padding:12px 0 16px;box-shadow:0 -10px 18px -14px rgba(11,11,12,.16)}',
    '.ndc-card .ndc-rep-foot{padding:12px 20px 16px}',
    // 콘텐츠
    '.ndc-rep{font-size:13.5px;line-height:1.65;color:#3a3a40;font-family:inherit}',
    '.ndc-rep>*{animation:ndc-fadeup .45s cubic-bezier(.22,1,.36,1) both}',
    '.ndc-rep>*:nth-child(2){animation-delay:.05s}.ndc-rep>*:nth-child(3){animation-delay:.1s}.ndc-rep>*:nth-child(4){animation-delay:.15s}.ndc-rep>*:nth-child(5){animation-delay:.2s}.ndc-rep>*:nth-child(6){animation-delay:.25s}.ndc-rep>*:nth-child(7){animation-delay:.3s}.ndc-rep>*:nth-child(n+8){animation-delay:.35s}',
    '.ndc-rep-top{display:flex;align-items:flex-start;gap:10px;margin-bottom:2px}',
    '.ndc-rep-eyebrow{font-size:10.5px;font-weight:800;letter-spacing:.16em;color:#9a9aa2}',
    '.ndc-rep-level{margin-left:auto;padding:4px 12px;border-radius:999px;background:#16a34a;color:#fff;font-weight:800;font-size:11.5px;white-space:nowrap;box-shadow:0 8px 18px -8px rgba(22,163,74,.6)}',
    '.ndc-rep-x{margin-left:auto;background:none;border:none;color:#b5b5bc;font-size:19px;line-height:1;cursor:pointer;padding:0 0 0 8px}',
    '.ndc-rep-x:hover{color:#55555c}',
    '.ndc-rep-level+.ndc-rep-x{margin-left:0}',
    '.ndc-rep-title{margin:0 0 6px;font-size:18px;font-weight:800;letter-spacing:-0.02em;color:#0b0b0c}',
    '.ndc-rep-headline{margin:0 0 16px;color:#55555c;font-size:14px}',
    '.ndc-rep-stats{display:flex;border:1px solid #ececef;border-radius:14px;background:#fff;margin-bottom:12px;overflow:hidden}',
    '.ndc-rep-stat{flex:1;padding:11px 8px;text-align:center}',
    '.ndc-rep-stat+.ndc-rep-stat{border-left:1px solid #ececef}',
    '.ndc-rep-stat b{display:block;font-size:15px;font-weight:800;color:#0b0b0c;letter-spacing:-0.01em}',
    '.ndc-rep-stat span{display:block;margin-top:1px;font-size:11px;color:#9a9aa2;font-weight:600}',
    '.ndc-rep-share{margin:0 0 6px;padding:12px 14px;border:1px solid #ececef;border-radius:14px;background:#fff}',
    '.ndc-rep-share-label{display:flex;justify-content:space-between;align-items:baseline;font-size:12px;color:#55555c;font-weight:600;margin-bottom:7px}',
    '.ndc-rep-share-label strong{font-size:15px;color:#15803d;letter-spacing:-0.01em}',
    '.ndc-rep-bar{height:8px;border-radius:999px;background:#ececef;overflow:hidden}',
    '.ndc-rep-bar i{display:block;height:100%;border-radius:999px;background:linear-gradient(90deg,#22c55e,#16a34a);animation:ndc-grow .7s .2s cubic-bezier(.22,1,.36,1) backwards}',
    '.ndc-rep-share-sub{display:flex;justify-content:space-between;margin-top:6px;font-size:11px;color:#9a9aa2}',
    '.ndc-rep-sec{padding:13px 2px 2px;margin-top:9px;border-top:1px solid #f0f0f2}',
    '.ndc-rep-sec h4{margin:0 0 7px;font-size:12px;font-weight:800;letter-spacing:.01em;color:#15803d}',
    '.ndc-rep-sec ul{margin:0;padding:0;list-style:none}',
    '.ndc-rep-sec li{position:relative;margin:4px 0;padding-left:15px}',
    '.ndc-rep-sec li::before{content:"";position:absolute;left:1px;top:9px;width:5px;height:5px;border-radius:50%;background:#16a34a}',
    '.ndc-rep-suggest{margin:14px 0 16px;padding:15px 17px;border-radius:16px;background:linear-gradient(135deg,#15803d,#166534);color:#fff;box-shadow:0 14px 30px -16px rgba(21,128,61,.55)}',
    '.ndc-rep-suggest h4{margin:0 0 6px;font-size:11px;font-weight:800;letter-spacing:.12em;color:rgba(255,255,255,.75)}',
    '.ndc-rep-suggest p{margin:3px 0;font-size:14px;font-weight:700;line-height:1.55;letter-spacing:-0.01em}',
    '.ndc-rep-again{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;margin:0;padding:14px;border:none;border-radius:999px;background:#16a34a;color:#fff;font:inherit;font-weight:800;font-size:14.5px;cursor:pointer;box-shadow:0 16px 34px -14px rgba(22,163,74,.7);transition:transform .15s ease,box-shadow .15s ease}',
    '.ndc-rep-again:hover{transform:translateY(-1px);box-shadow:0 20px 40px -14px rgba(22,163,74,.75)}',
    '.ndc-rep-again svg{flex:none}',
    // 통화 중 카드 — 리포트 카드(.ndc-card)와 같은 껍데기를 쓰고 머리·푸터만 다르다
    '.ndc-live-top{display:flex;align-items:center;gap:10px;margin-bottom:2px}',
    '.ndc-live-eyebrow{display:inline-flex;align-items:center;gap:7px;font-size:10.5px;font-weight:800;letter-spacing:.16em;color:#15803d}',
    '.ndc-live-eyebrow i{width:8px;height:8px;border-radius:50%;background:#16a34a;animation:ndc-blink 1.4s ease-in-out infinite}',
    '.ndc-live-time{margin-left:auto;font-size:13.5px;font-weight:800;color:#15803d;font-variant-numeric:tabular-nums}',
    '.ndc-rep-end{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;margin:0;padding:13px;border:1px solid #e2e2e8;border-radius:999px;background:#fff;color:#55555c;font:inherit;font-weight:800;font-size:14.5px;cursor:pointer;transition:border-color .15s ease,color .15s ease}',
    '.ndc-rep-end:hover{border-color:#c9c9d0;color:#0b0b0c}',
    // 로딩 스켈레톤
    '.ndc-rep-load{display:flex;align-items:center;gap:9px;margin:2px 0 14px;color:#55555c;font-size:13.5px;font-weight:600}',
    '.ndc-rep-load i{width:9px;height:9px;border-radius:50%;background:#16a34a;animation:ndc-pulse 1.2s ease-out infinite;box-shadow:0 0 0 0 rgba(22,163,74,.4)}',
    '.ndc-skel{height:12px;border-radius:6px;margin:10px 0;background:linear-gradient(90deg,#f0f0f2 25%,#e6e6ea 37%,#f0f0f2 63%);background-size:400px 100%;animation:ndc-shimmer 1.3s linear infinite}',
    '.ndc-skel:nth-child(3){width:82%}.ndc-skel:nth-child(4){width:64%}.ndc-skel:last-child{width:44%;margin-bottom:20px}',
  ].join('\n')
  document.head.appendChild(s)
}

/* ── 통화 중 버튼 UI ── */

function fmtElapsed(startedAt: number): string {
  const s = Math.max(0, Math.round((Date.now() - startedAt) / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

function renderLiveBtn(btn: HTMLElement, startedAt: number) {
  btn.innerHTML =
    '<span class="ndc-dot"></span>통화 중 ' +
    `<span data-ndc-time>${fmtElapsed(startedAt)}</span>` +
    '<span class="ndc-status">&nbsp;· 누르면 종료</span>'
}

/** 홈 데모 패널의 마이크 오브 — 있으면 통화 중 살아 움직이게 (없어도 무해). */
function findPanelOrb(btn: HTMLElement): Element | null {
  const panel = btn.closest('[data-demo-panel]')
  return panel?.querySelector('span[style*="radial-gradient"]') ?? null
}

function setLabel(btn: HTMLElement, text: string) {
  btn.textContent = text
}

function restoreBtn(btn: HTMLElement, originalHtml: string) {
  btn.innerHTML = originalHtml
}

function flashLabel(btn: HTMLElement, originalHtml: string, text: string) {
  setLabel(btn, text)
  setTimeout(() => restoreBtn(btn, originalHtml), 2500)
}

/* ── 통화 중 카드 (우하단) ──
 *
 * 종료 리포트가 뜨던 자리에 통화가 시작될 때부터 카드를 띄운다. 껍데기(.ndc-card)와
 * 안쪽 스크롤 영역은 리포트 카드와 같은 것을 쓰므로, 종료 시 새로 만들지 않고
 * 내용만 갈아끼우면 그대로 리포트로 이어진다.
 */

function liveCardHtml(startedAt: number): string {
  return (
    '<div class="ndc-rep">' +
    '<div class="ndc-live-top"><span class="ndc-live-eyebrow"><i></i>ON CALL</span>' +
    `<span class="ndc-live-time" data-ndc-card-time>${fmtElapsed(startedAt)}</span></div>` +
    '<h3 class="ndc-rep-title">에이전트와 통화하고 있어요</h3>' +
    '<p class="ndc-rep-headline">편하게 말을 걸어보세요. 통화를 마치면 방금 대화를 분석해 리포트로 정리해 드려요.</p>' +
    '</div>'
  )
}

function liveFooterHtml(): string {
  return '<div class="ndc-rep-foot"><button type="button" class="ndc-rep-end">통화 종료하기</button></div>'
}

/** 우하단 카드를 걷어낸다 — 통화 중 카드와 리포트 카드가 같은 자리를 쓴다. */
function removeCard() {
  document.querySelector('.ndc-card')?.remove()
  liveCard = null
}

/**
 * 통화가 시작되면 우하단에 통화 중 카드를 띄운다.
 *
 * 홈 데모 패널에서 시작한 통화는 패널이 이미 같은 역할(통화 중 화면 → 리포트)을 하므로
 * 만들지 않는다 — 만들면 패널과 카드가 동시에 떠서 팝업이 두 개가 된다.
 */
function showLiveCard(btn: HTMLElement, startedAt: number) {
  if (btn.closest('[data-demo-panel]')) return
  removeCard()
  const card = document.createElement('div')
  card.className = 'ndc-card'
  card.setAttribute('role', 'dialog')
  card.setAttribute('aria-label', '통화 중')
  const scroll = document.createElement('div')
  scroll.className = 'ndc-card-scroll'
  scroll.innerHTML = liveCardHtml(startedAt)
  card.appendChild(scroll)
  card.insertAdjacentHTML('beforeend', liveFooterHtml())
  // 카드의 종료 버튼은 통화 버튼을 누른 것과 같다 — end_reason 도 'button' 그대로 둔다
  // (시트에 없는 값을 새로 만들면 GA4 에 존재하지 않는 세그먼트가 생긴다).
  card.addEventListener('click', (e) => {
    if ((e.target as Element).closest('.ndc-rep-end')) cleanupCall('button')
  })
  document.body.appendChild(card)
  liveCard = card
}

function endBackendSession(endUrl: string) {
  // keepalive: 종료 직후 페이지가 떠나도 요청이 살아남게
  // 토큰은 헤더가 아니라 text/plain 본문으로 — 사용자 정의 헤더는 교차 출처 preflight 를
  // 부르는데 언로드 중엔 그게 막힐 수 있고, pagehide 의 sendBeacon 은 헤더를 못 붙인다.
  fetch(endUrl, { method: 'POST', keepalive: true, body: endBody(endUrl) }).catch(() => {})
}

function cleanupCall(reason: 'button' | 'close' | 'disconnected' | 'error') {
  if (!active) return
  const call = active
  active = null
  clearInterval(call.timerId)
  call.orbEl?.classList.remove('ndc-orb-live')
  const durationSec = Math.round((Date.now() - call.startedAt) / 1000)
  ga('try_call_end', { demo_id: call.slug, duration_sec: durationSec, end_reason: reason })
  endBackendSession(call.endUrl)
  try {
    call.room.disconnect()
  } catch {
    /* already disconnected */
  }
  document.querySelectorAll('audio[data-demo-call-track]').forEach((el) => el.remove())
  restoreBtn(call.btn, call.originalHtml)

  // 통화 종료 리포트 — 아주 짧은 통화는 제외
  if (reason === 'error' || durationSec < 8 || !REPORT_SLUGS.has(call.slug)) {
    removeCard() // 이어받을 리포트가 없으면 통화 중 카드도 같이 접는다
    return
  }
  const reportUrl = call.endUrl.replace(/\/end$/, '/report')
  const panelEl = call.btn.closest('[data-demo-panel]') as HTMLElement | null
  if (panelEl && reason !== 'close') {
    // 홈: 같은 패널 안에서 리포트로 전환 — 팝업이 늘어나지 않는다
    showReportInPanel(panelEl, call.slug, reportUrl)
  } else {
    // 패널 × 종료(패널이 닫히는 의도) 또는 유즈케이스 페이지 → 단독 카드
    showReportCard(call.slug, reportUrl, call.btn)
  }
}

/* ── 세션 리포트 — 유즈케이스 스펙 "산출물(종료 시)" ── */

const REPORT_SLUGS = new Set([
  'neona-guide',
  'english-tutor',
  'math-tutor',
  'cs-agent',
  'reception',
  'companion',
])
const REPORT_POLL_MS = 2500
const REPORT_POLL_MAX = 14 // ≈35초 — transcript 저장(비동기) + 생성 대기

interface DemoReport {
  title: string
  headline: string
  level: string | null
  sections: { title: string; items: string[]; suggest?: boolean }[]
  stats: {
    duration_sec: number
    user_turns: number
    user_chars: number
    agent_chars?: number
  }
}

function esc(s: string): string {
  const div = document.createElement('div')
  div.textContent = s
  return div.innerHTML
}

function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60)
  return m > 0 ? `${m}분 ${sec % 60}초` : `${sec}초`
}

const RESTART_ICON =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3z"/><path d="M6 11a6 6 0 0 0 12 0"/><path d="M12 17v4"/></svg>'

function loadingHtml(): string {
  return (
    '<div class="ndc-rep">' +
    '<p class="ndc-rep-load"><i></i>방금 대화를 분석해 리포트를 만들고 있어요</p>' +
    '<div class="ndc-skel"></div><div class="ndc-skel"></div><div class="ndc-skel"></div>' +
    '</div>'
  )
}

/** 리포트 본문 HTML — 표면(카드/패널)과 무관한 공통 렌더러. */
function reportHtml(r: DemoReport, opts: { showClose: boolean }): string {
  const stats = r.stats || { duration_sec: 0, user_turns: 0, user_chars: 0 }
  const agentChars = stats.agent_chars ?? 0
  const totalChars = stats.user_chars + agentChars
  const userShare = totalChars > 0 ? Math.round((stats.user_chars / totalChars) * 100) : 0

  const top =
    '<div class="ndc-rep-top"><span class="ndc-rep-eyebrow">SESSION REPORT</span>' +
    (r.level ? `<span class="ndc-rep-level">${esc(r.level)}</span>` : '') +
    (opts.showClose
      ? '<button type="button" class="ndc-rep-x" aria-label="닫기">×</button>'
      : '') +
    '</div>'

  const statCells =
    '<div class="ndc-rep-stats">' +
    `<div class="ndc-rep-stat"><b>${esc(fmtDuration(stats.duration_sec))}</b><span>대화 시간</span></div>` +
    `<div class="ndc-rep-stat"><b>${stats.user_turns}회</b><span>내 발화</span></div>` +
    `<div class="ndc-rep-stat"><b>${stats.user_chars}자</b><span>말한 분량</span></div>` +
    '</div>'

  const shareBar =
    totalChars > 0
      ? '<div class="ndc-rep-share">' +
        `<div class="ndc-rep-share-label"><span>내 발화 비중</span><strong>${userShare}%</strong></div>` +
        `<div class="ndc-rep-bar"><i style="width:${userShare}%"></i></div>` +
        `<div class="ndc-rep-share-sub"><span>나 ${stats.user_chars}자</span><span>에이전트 ${agentChars}자</span></div>` +
        '</div>'
      : ''

  // 백엔드가 suggest 플래그를 준 섹션만 하이라이트 — 조건부 섹션이 생략돼도
  // 위치 기반 오판 없음. 플래그가 하나도 없으면 전부 일반 섹션으로 렌더.
  const sections = r.sections || []
  const sectionHtml = sections
    .map((sec) =>
      sec.suggest
        ? `<div class="ndc-rep-suggest"><h4>${esc(sec.title)}</h4>` +
          sec.items.map((i) => `<p>${esc(i)}</p>`).join('') +
          '</div>'
        : `<div class="ndc-rep-sec"><h4>${esc(sec.title)}</h4><ul>` +
          sec.items.map((i) => `<li>${esc(i)}</li>`).join('') +
          '</ul></div>',
    )
    .join('')

  return (
    '<div class="ndc-rep">' +
    top +
    `<h3 class="ndc-rep-title">${esc(r.title || '세션 리포트')}</h3>` +
    `<p class="ndc-rep-headline">${esc(r.headline)}</p>` +
    statCells +
    shareBar +
    sectionHtml +
    '</div>'
  )
}

/** 하단 고정 푸터 — 스크롤과 무관하게 "다시 대화하기" 통로가 항상 보인다. */
function footerHtml(): string {
  return `<div class="ndc-rep-foot"><button type="button" class="ndc-rep-again">${RESTART_ICON}이어서 다시 대화하기</button></div>`
}

/** 공통 폴링 — ready 시 render 콜백, 실패·중단 시 cleanup 콜백. */
async function pollReport(
  reportUrl: string,
  stillMounted: () => boolean,
  onReady: (r: DemoReport) => void,
  onGiveUp: () => void,
) {
  for (let attempt = 0; attempt < REPORT_POLL_MAX; attempt++) {
    if (!stillMounted()) return
    try {
      const res = await fetch(reportUrl, { headers: visitorHeaders(reportUrl) })
      if (res.ok) {
        const data = (await res.json()) as { status: string; report?: DemoReport }
        if (data.status === 'ready' && data.report) {
          if (stillMounted()) onReady(data.report)
          return
        }
        if (data.status !== 'pending') break // unsupported·unavailable — 조용히 접기
      }
    } catch {
      /* 네트워크 오류 — 다음 폴링에서 재시도 */
    }
    if (attempt < REPORT_POLL_MAX - 1) {
      await new Promise((resolve) => setTimeout(resolve, REPORT_POLL_MS))
    }
  }
  if (stillMounted()) onGiveUp()
}

/** 리포트에서 "다시 대화하기" — 홈이면 패널을 다시 열고 배선된 버튼으로 재통화. */
function restartCall(slug: string, fromPanel: boolean) {
  if (fromPanel) {
    ;(document.querySelector('button[data-demo-open]') as HTMLElement | null)?.click()
  }
  const btn = fromPanel
    ? (document.querySelector('[data-demo-panel] a[data-demo-call]') as HTMLElement | null)
    : (document.querySelector(`a[data-demo-call="${slug}"]`) as HTMLElement | null)
  btn?.click()
}

/* ── 표면 1: 홈 데모 패널 내부 전환 ── */

// 패널 본문(마이크 오브 영역)의 원본 HTML/스타일 — 리포트 전환 후 복원용
let panelSwap: { bodyEl: HTMLElement; html: string; cssText: string } | null = null

function panelBody(panelEl: HTMLElement): HTMLElement | null {
  // 패널 카드(첫 div) 안의 마지막 자식 = 본문 영역 (헤더 스트립 다음)
  const card = panelEl.firstElementChild as HTMLElement | null
  const body = card?.lastElementChild as HTMLElement | null
  return body && body !== card?.firstElementChild ? body : null
}

function restorePanelBody() {
  if (!panelSwap) return
  panelSwap.bodyEl.classList.remove('ndc-inpanel')
  panelSwap.bodyEl.style.cssText = panelSwap.cssText
  panelSwap.bodyEl.innerHTML = panelSwap.html
  panelSwap = null
}

function showReportInPanel(panelEl: HTMLElement, slug: string, reportUrl: string) {
  const bodyEl = panelBody(panelEl)
  if (!bodyEl) {
    // 마크업 드리프트 등으로 본문을 못 찾으면 단독 카드로 폴백
    showReportCard(slug, reportUrl, panelEl)
    return
  }
  if (!panelSwap) {
    panelSwap = { bodyEl, html: bodyEl.innerHTML, cssText: bodyEl.style.cssText }
  }
  bodyEl.classList.add('ndc-inpanel')
  bodyEl.style.cssText = '' // 원본 인라인(display:flex·align-items:center 등) 무력화
  bodyEl.innerHTML = loadingHtml()
  bodyEl.onclick = (e) => {
    if ((e.target as Element).closest('.ndc-rep-again')) {
      bodyEl.onclick = null
      restorePanelBody()
      restartCall(slug, true)
    }
  }
  const mounted = () => panelSwap?.bodyEl === bodyEl && !panelEl.hidden
  pollReport(
    reportUrl,
    mounted,
    (r) => {
      // 패널 자체 × 가 있으므로 리포트에는 닫기 버튼을 넣지 않는다
      bodyEl.innerHTML = reportHtml(r, { showClose: false }) + footerHtml()
    },
    () => {
      bodyEl.onclick = null
      restorePanelBody()
    },
  )
}

/* ── 표면 2: 단독 플로팅 카드 (유즈케이스 페이지 / 패널 × 종료) ── */

function showReportCard(slug: string, reportUrl: string, anchor: HTMLElement) {
  // 통화 중 카드가 떠 있으면 그 DOM 을 이어받는다 — 카드가 사라졌다 다시 뜨지 않고
  // 같은 자리에서 내용만 "분석 중 → 리포트" 로 바뀐다.
  const reused = liveCard
  liveCard = null
  let card: HTMLElement
  let scroll: HTMLElement
  if (reused) {
    card = reused
    card.setAttribute('aria-label', '세션 리포트')
    card.querySelector('.ndc-rep-foot')?.remove() // 통화 종료 버튼 → 리포트 푸터로 교체
    scroll = card.querySelector('.ndc-card-scroll') as HTMLElement
    scroll.innerHTML = loadingHtml()
  } else {
    document.querySelector('.ndc-card')?.remove()
    card = document.createElement('div')
    card.className = 'ndc-card'
    card.setAttribute('role', 'dialog')
    card.setAttribute('aria-label', '세션 리포트')
    scroll = document.createElement('div')
    scroll.className = 'ndc-card-scroll'
    scroll.innerHTML = loadingHtml()
    card.appendChild(scroll)
    document.body.appendChild(card)
  }
  const fromPanel = !!anchor.closest('[data-demo-panel]')
  card.addEventListener('click', (e) => {
    const target = e.target as Element
    if (target.closest('.ndc-rep-x')) {
      card.remove()
      return
    }
    if (target.closest('.ndc-rep-again')) {
      card.remove()
      restartCall(slug, fromPanel)
    }
  })
  pollReport(
    reportUrl,
    () => card.isConnected,
    (r) => {
      scroll.innerHTML = reportHtml(r, { showClose: true })
      card.insertAdjacentHTML('beforeend', footerHtml())
    },
    () => card.remove(),
  )
}

/* ── 통화 시작 ── */

/**
 * 핸드오프 녹음 재생(landing/assets/audio.js)을 멈춘다.
 *
 * 홈에는 녹음 스피커와 인페이지 실통화가 같이 놓여 있는데 두 시스템이 서로를
 * 모른다. 녹음이 흐르는 채로 통화를 시작하면 두 소리가 겹쳐 들리고, 마이크가
 * 켜지므로 녹음이 에이전트에게 새어 들어갈 수도 있다.
 *
 * 켜져 있는 스피커 버튼을 클릭해 각 컴포넌트의 자체 토글 로직으로 끈다 —
 * 재생 중지와 아이콘·내부 상태 복원이 한 번에 맞는다(토글 상태가 클로저에 있어
 * 밖에서 직접 되돌릴 수 없다). 아이콘 규약은 켜짐 = [data-sound-on] 이 보이는 것.
 */
function stopHandoffAudio() {
  document.querySelectorAll<HTMLElement>('[data-sound-on]').forEach((icon) => {
    if (icon.hidden) return // 이미 꺼진 버튼
    icon.closest('button')?.click()
  })
  // 버튼 상태와 실제 재생이 어긋난 경우의 보루.
  ;(
    window as Window & { Neona?: { stopSequence?: () => void } }
  ).Neona?.stopSequence?.()
}

async function startCall(btn: HTMLElement, slug: string, token: string) {
  busy = true
  stopHandoffAudio()
  ensureStyle()
  removeCard() // 이전 통화 리포트는 접는다
  const originalHtml = btn.innerHTML
  setLabel(btn, '연결 중…')
  let endUrl = ''
  try {
    const res = await fetch(`${API_BASE}/try/${token}/sessions`, {
      method: 'POST',
      headers: { 'X-Neona-Visitor-Bind': '1' },
    })
    if (!res.ok) throw new Error(`session create failed (${res.status})`)
    const data = (await res.json()) as {
      session_id: string
      token: string
      server_url: string
      visitor_token?: string
    }
    endUrl = `${API_BASE}/try/${token}/sessions/${data.session_id}/end`
    if (data.visitor_token) {
      visitorTokenByUrl.set(endUrl, data.visitor_token)
      visitorTokenByUrl.set(endUrl.replace(/\/end$/, '/report'), data.visitor_token)
    }

    const { Room, RoomEvent, Track } = await import('livekit-client')
    const room = new Room({ adaptiveStream: true, dynacast: true })

    room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
      if (track.kind !== Track.Kind.Audio) return
      const el = track.attach()
      el.setAttribute('data-demo-call-track', track.sid ?? '')
      el.autoplay = true
      document.body.appendChild(el)
    })
    room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
      track.detach().forEach((el) => el.remove())
    })
    room.on(RoomEvent.Disconnected, () => cleanupCall('disconnected'))

    await room.connect(data.server_url, data.token)
    try {
      await room.localParticipant.setMicrophoneEnabled(true)
    } catch {
      room.disconnect()
      endBackendSession(endUrl)
      flashLabel(btn, originalHtml, '마이크 권한을 허용해주세요')
      return
    }
    if (!room.canPlaybackAudio) {
      try {
        await room.startAudio()
      } catch {
        /* 사용자 제스처로 시작됐으므로 사실상 도달하지 않음 */
      }
    }

    const startedAt = Date.now()
    // 시트: 홈은 position, 활용사례 상세는 use_case 를 함께 요구한다.
    //
    // position 은 마크업에 근거가 있을 때만 보낸다. v2.1 핸드오프에는 데모 버튼을
    // 구분할 훅이 없어(조상 id 가 전부 null) 무엇을 넣어도 추측이 된다 — 틀린 값을
    // 보내면 GA4 에 존재하지 않는 세그먼트가 생기고, 그게 맞는 줄 알고 판단하게 된다.
    // v2.2: 빌드가 전 CTA 에 data-cta-position 을 보장하므로 항상 채워진다.
    const page = (globalThis as { NEONA_PAGE?: string }).NEONA_PAGE || ''
    const position = btn.closest('[data-cta-position]')?.getAttribute('data-cta-position')
    ga('try_call_start', {
      demo_id: slug,
      ...(position ? { position } : {}),
      ...(page.startsWith('uc_') ? { use_case: slug } : {}),
    })
    const orbEl = findPanelOrb(btn)
    orbEl?.classList.add('ndc-orb-live')
    renderLiveBtn(btn, startedAt)
    showLiveCard(btn, startedAt)
    // 지속 이벤트는 이미 도는 1초 타이머에 얹는다 — 타이머를 새로 만들지 않는다.
    let lastMark = 0
    const timerId = window.setInterval(() => {
      const elapsed = Math.round((Date.now() - startedAt) / 1000)
      const timeEl = btn.querySelector('[data-ndc-time]')
      if (timeEl) timeEl.textContent = fmtElapsed(startedAt)
      const cardTimeEl = liveCard?.querySelector('[data-ndc-card-time]')
      if (cardTimeEl) cardTimeEl.textContent = fmtElapsed(startedAt)
      for (const mark of PROGRESS_MARKS) {
        if (elapsed >= mark && lastMark < mark) {
          lastMark = mark
          ga('try_call_progress', { demo_id: slug, sec: mark })
        }
      }
    }, 1000)

    // 에이전트 발화 중이면 점이 빠르게 깜빡인다 (텍스트 폭 변화 없이 상태만)
    room.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
      const agentSpeaking = speakers.some((p) => p !== room.localParticipant)
      btn.querySelector('.ndc-dot')?.classList.toggle('ndc-dot--speaking', agentSpeaking)
    })

    active = { room, endUrl, btn, originalHtml, startedAt, slug, timerId, orbEl }
  } catch {
    if (endUrl) endBackendSession(endUrl)
    flashLabel(btn, originalHtml, '연결에 실패했어요 — 다시 시도')
  } finally {
    busy = false
  }
}

// capture 단계에서 가로채 원본 핸들러(home.js 의 voice-demo 리스너 등)보다 먼저 처리
document.addEventListener(
  'click',
  (e) => {
    const target = e.target as Element | null
    // 홈 데모 패널의 × 닫기 — 통화 중이면 "패널 닫힘 = 통화 종료".
    // stopPropagation 하지 않아 home.js 의 패널 숨김도 그대로 동작한다.
    if (target?.closest?.('[data-demo-close]')) {
      if (active) cleanupCall('close')
      else restorePanelBody() // 패널 안 리포트가 떠 있었다면 다음 열기를 위해 복원
      return
    }
    const btn = target?.closest?.('[data-demo-call]') as HTMLElement | null
    if (!btn) return
    const slug = btn.getAttribute('data-demo-call') || ''
    const token = demoLinks()[slug]
    if (!token) return // 매핑 미주입 — 기존 href(문의 폼)로 그대로 진행
    e.preventDefault()
    e.stopPropagation()
    if (busy) return
    if (active) {
      cleanupCall('button')
      return
    }
    void startCall(btn, slug, token)
  },
  true,
)

// 통화 중 페이지 이탈 — sendBeacon 으로 세션 종료(놓치면 orphan cron 이 정리)
//
// try_call_end 도 여기서 쏜다. 탭을 닫는 건 데모를 끝내는 가장 흔한 방법인데,
// cleanupCall 은 이 경로로 오지 않는다 — 안 쏘면 시작 대비 종료가 구조적으로 모자라고
// 통화 시간 분포가 "버튼으로 끊은 사람"쪽으로 치우친다.
// gtag 는 언로드 중 sendBeacon 으로 전송하므로 도달률이 완벽하진 않아도 유의미하다.
window.addEventListener('pagehide', () => {
  if (!active) return
  // 종료 대상을 먼저 떼어내고 타이머를 멈춘다. 그러지 않으면 bfcache 복귀나
  // 뒤늦은 RoomEvent.Disconnected 가 cleanupCall 을 태워 같은 통화에 종료 이벤트와
  // 종료 요청이 두 번 나간다(CodeRabbit 지적).
  const call = active
  active = null
  clearInterval(call.timerId)
  ga('try_call_end', {
    demo_id: call.slug,
    duration_sec: Math.round((Date.now() - call.startedAt) / 1000),
    end_reason: 'pagehide',
  })
  navigator.sendBeacon(call.endUrl, endBody(call.endUrl))
})
